/**
 * React-Query hooks for the backend booking attachments endpoints:
 *   GET    /api/v1/bookings/{id}/comments
 *   POST   /api/v1/bookings/{id}/comments
 *   DELETE /api/v1/bookings/{id}/comments/{commentId}
 *   GET    /api/v1/bookings/{id}/files
 *   POST   /api/v1/bookings/{id}/files     (multipart/form-data)
 *   DELETE /api/v1/bookings/{id}/files/{fileId}
 *
 * Goes through `authFetchJson` (same pattern as `BookingHistoryTab`) so a
 * 401 is handled by the session-expired refresh flow, and so we don't need
 * to wait for the OpenAPI types to be regenerated to ship this. When the
 * types catch up via `npm run gen:api`, callers can swap to the typed
 * `api.GET / POST / DELETE` helpers without changing the component shape.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { authFetch, authFetchJson } from "./auth-fetch";
import { useAuthStore } from "./auth";

/** Helper for endpoints that return 204 No Content. */
async function authFetchVoid(
  input: RequestInfo | URL,
  init?: RequestInit,
): Promise<void> {
  const res = await authFetch(input, init);
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(
      (body as { message?: string }).message ?? `HTTP ${res.status}`,
    );
  }
}

/* ---------- Types (mirror backend DTOs in BookingController) ---------- */

export type BookingComment = {
  id: string;
  body: string;
  authorEmail: string;
  createdAt: string;
};
type CommentsResponse = { entries: BookingComment[] };

export type BookingFile = {
  id: string;
  filename: string;
  mime: string;
  bytes: number;
  downloadUrl: string;
  uploadedAt: string;
  uploadedBy: string;
};
type FilesResponse = { entries: BookingFile[] };

/* ---------- Comments ---------- */

export function useBookingComments(bookingId: string | null | undefined) {
  const accessToken = useAuthStore((s) => s.accessToken);
  return useQuery({
    queryKey: ["booking", bookingId, "comments"],
    enabled: !!accessToken && !!bookingId,
    queryFn: () =>
      authFetchJson<CommentsResponse>(
        `/api/v1/bookings/${bookingId}/comments`,
      ),
  });
}

export function useAddBookingComment(bookingId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: string) =>
      authFetchJson<BookingComment>(`/api/v1/bookings/${bookingId}/comments`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ body }),
      }),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["booking", bookingId, "comments"] }),
  });
}

export function useDeleteBookingComment(bookingId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (commentId: string) =>
      authFetchVoid(
        `/api/v1/bookings/${bookingId}/comments/${commentId}`,
        { method: "DELETE" },
      ),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["booking", bookingId, "comments"] }),
  });
}

/* ---------- Files ---------- */

export function useBookingFiles(bookingId: string | null | undefined) {
  const accessToken = useAuthStore((s) => s.accessToken);
  return useQuery({
    queryKey: ["booking", bookingId, "files"],
    enabled: !!accessToken && !!bookingId,
    queryFn: () =>
      authFetchJson<FilesResponse>(`/api/v1/bookings/${bookingId}/files`),
  });
}

export function useAddBookingFile(bookingId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => {
      const fd = new FormData();
      fd.append("file", file);
      // NOTE: don't set Content-Type — the browser sets the
      // multipart boundary automatically.
      return authFetchJson<BookingFile>(
        `/api/v1/bookings/${bookingId}/files`,
        { method: "POST", body: fd },
      );
    },
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["booking", bookingId, "files"] }),
  });
}

export function useDeleteBookingFile(bookingId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (fileId: string) =>
      authFetchVoid(`/api/v1/bookings/${bookingId}/files/${fileId}`, {
        method: "DELETE",
      }),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["booking", bookingId, "files"] }),
  });
}
